const FREE_NEURAL_HQ_KEY = 'la_autotune_neural_hq_free_uses';
const FREE_APPLY_KEY = 'la_autotune_apply_uses';

// Free daily AI Natural HQ full renders. Anonymous visitors get 1/day;
// registered free accounts get 2/day (a real, honest reason to sign up).
// Pro never consults this module — it is unlimited.
export const FREE_NEURAL_HQ_USES_PER_DAY_ANON = 1;
export const FREE_NEURAL_HQ_USES_PER_DAY_REGISTERED = 2;
export const NEURAL_HQ_SECONDS_PER_CREDIT = 60;

// Free daily autotune Applies (full renders, ANY engine — classic/Lyra/
// neural all count; the neural quota above still applies on top for the
// neural engine). Tightened twice on 2026-07-21 (3/10 → 1/3 → 0/1), then
// the anonymous tier was walked back to 1 on 2026-07-26.
//
// Why the walk-back: at 0, an anonymous visitor could not finish a single
// Apply, so `free_tool_completed` never fired — and that event is what
// triggers the completion-moment Pro upsell. Western paywall_view went
// 11-16/day → 0 within a day of the change (the 0-quota wall routes
// anonymous users to the AuthModal, never to pricing), and begin_checkout
// followed it down. Both actual paying customers converted at a value
// moment, not at a wall. One Apply restores the moment; the wall still
// lands on the second one, which is where the signup ask belongs.
// Consumed on successful render only, never on failures.
//
// Registered goes back to 3 (the intermediate 2026-07-21 value) because the
// anonymous tier is 1 again: with both at 1 the signup gate would promise
// nothing, and the AuthModal pitch has to be true.
export const FREE_APPLY_USES_PER_DAY_ANON = 1;
export const FREE_APPLY_USES_PER_DAY_REGISTERED = 3;

// Free-plan AI Preview window (seconds). Previews run the real neural engine
// so the quality difference is genuine, but on a capped window — they never
// consume the daily quota or credits.
export const FREE_AI_PREVIEW_WINDOW_SEC = 15;

type DailyUseRecord = {
  day: string;
  count: number;
};

export function getNeuralHqDailyLimit(loggedIn: boolean): number {
  return loggedIn ? FREE_NEURAL_HQ_USES_PER_DAY_REGISTERED : FREE_NEURAL_HQ_USES_PER_DAY_ANON;
}

export function getNeuralHqCreditsRequired(durationSec: number): number {
  const seconds = Number.isFinite(durationSec) ? Math.max(1, durationSec) : 1;
  return Math.max(1, Math.ceil(seconds / NEURAL_HQ_SECONDS_PER_CREDIT));
}

export function getNeuralHqFreeUsesRemaining(loggedIn: boolean): number {
  const record = readDailyUseRecord(FREE_NEURAL_HQ_KEY);
  return Math.max(0, getNeuralHqDailyLimit(loggedIn) - record.count);
}

export function consumeNeuralHqFreeUse(): void {
  const record = readDailyUseRecord(FREE_NEURAL_HQ_KEY);
  writeDailyUseRecord(FREE_NEURAL_HQ_KEY, { ...record, count: record.count + 1 });
}

export function getApplyDailyLimit(loggedIn: boolean): number {
  return loggedIn ? FREE_APPLY_USES_PER_DAY_REGISTERED : FREE_APPLY_USES_PER_DAY_ANON;
}

export function getApplyUsesRemaining(loggedIn: boolean): number {
  const record = readDailyUseRecord(FREE_APPLY_KEY);
  return Math.max(0, getApplyDailyLimit(loggedIn) - record.count);
}

export function consumeApplyUse(): void {
  const record = readDailyUseRecord(FREE_APPLY_KEY);
  writeDailyUseRecord(FREE_APPLY_KEY, { ...record, count: record.count + 1 });
}

function readDailyUseRecord(key: string): DailyUseRecord {
  const today = new Date().toISOString().slice(0, 10);
  if (typeof window === 'undefined') return { day: today, count: 0 };

  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) as DailyUseRecord : null;
    if (!parsed || parsed.day !== today) return { day: today, count: 0 };
    return { day: today, count: Math.max(0, Number(parsed.count) || 0) };
  } catch {
    return { day: today, count: 0 };
  }
}

function writeDailyUseRecord(key: string, record: DailyUseRecord): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(record));
  } catch {
    // Local quota storage is advisory. The server-side renderer must enforce
    // real usage limits once Neural HQ moves off-device.
  }
}
